import React from "react";
import { FiCalendar, FiImage, FiPlus, FiSave } from "react-icons/fi";
import { MdEvent } from "react-icons/md";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import {
  EVENT_CATEGORY_SUGGESTIONS,
  EVENTS_FORM_HELPERS,
} from "../data/eventsAdminData";

import {
  formatDateFr,
  formatDateTimeFr,
  getDateOnlyFromInput,
} from "../utils/eventsAdminUtils";

export default function EventsFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
  onOpenMediaPicker,
}) {
  const previewUrl = form.photo_vignette
    ? toAbsolutePublicUrl(form.photo_vignette)
    : "";

  const startPreview = form.start_date
    ? form.all_day
      ? formatDateFr(getDateOnlyFromInput(form.start_date))
      : formatDateTimeFr(form.start_date)
    : "Date de début non renseignée";

  const endPreview = form.end_date
    ? form.all_day
      ? formatDateFr(getDateOnlyFromInput(form.end_date))
      : formatDateTimeFr(form.end_date)
    : "";

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l’événement" : "Nouvel événement"}
      subtitle={
        isEdit
          ? "Mettez à jour l’événement puis enregistrez les modifications."
          : "Ajoutez un rendez-vous à l’agenda de la commune."
      }
      icon={MdEvent}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <AdminField
              label="Titre de l’événement"
              required
              error={fieldErrors.title}
              help="Exemple : Marché estival, réunion publique, cérémonie..."
            >
              <input
                value={form.title}
                onChange={(event) => onChange("title", event.target.value)}
                placeholder="Titre affiché dans l’agenda"
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>

            <AdminField label="Sous-titre" help="Information courte optionnelle.">
              <input
                value={form.subtitle}
                onChange={(event) => onChange("subtitle", event.target.value)}
                placeholder="Exemple : Place de la mairie"
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <AdminField label="Catégorie">
              <input
                value={form.categorie}
                onChange={(event) => onChange("categorie", event.target.value)}
                placeholder="Culture, Sport, Conseil municipal..."
                list="event-form-categories"
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />

              <datalist id="event-form-categories">
                {EVENT_CATEGORY_SUGGESTIONS.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </AdminField>

            <AdminField
              label="Lien externe"
              help="Billetterie, formulaire, site partenaire..."
            >
              <input
                value={form.external_link}
                onChange={(event) =>
                  onChange("external_link", event.target.value)
                }
                placeholder="https://..."
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <button
              type="button"
              onClick={() => onChange("all_day", !form.all_day)}
              className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.all_day
                  ? "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
                  : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  Événement sans horaire précis
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  À utiliser pour une journée complète ou un événement sur
                  plusieurs jours.
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.all_day ? "bg-sky-600" : "bg-slate-300 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.all_day ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <AdminField
              label={form.all_day ? "Date de début" : "Date et heure de début"}
              required
              error={fieldErrors.start_date}
            >
              <input
                type={form.all_day ? "date" : "datetime-local"}
                value={
                  form.all_day
                    ? getDateOnlyFromInput(form.start_date)
                    : form.start_date
                }
                onChange={(event) => onChange("start_date", event.target.value)}
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>

            <AdminField
              label={form.all_day ? "Date de fin" : "Date et heure de fin"}
              error={fieldErrors.end_date}
              help={
                form.all_day
                  ? "Optionnel si l’événement dure une seule journée."
                  : "Optionnel pour les événements courts."
              }
            >
              <input
                type={form.all_day ? "date" : "datetime-local"}
                value={
                  form.all_day
                    ? getDateOnlyFromInput(form.end_date)
                    : form.end_date
                }
                onChange={(event) => onChange("end_date", event.target.value)}
                className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>
          </div>

          <AdminField
            label="Vignette"
            help="Vous pouvez saisir une URL ou choisir une image dans la photothèque."
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                value={form.photo_vignette}
                onChange={(event) =>
                  onChange("photo_vignette", event.target.value)
                }
                placeholder="https://... ou /uploads/..."
                className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />

              <AdminButton
                icon={FiImage}
                variant="soft"
                onClick={onOpenMediaPicker}
                className="sm:min-w-[180px]"
              >
                Photothèque
              </AdminButton>
            </div>
          </AdminField>

          <AdminField
            label="Description"
            help="Utilisez les boutons de mise en forme pour structurer le texte."
          >
            <AdminRichTextEditor
              value={form.description}
              onChange={(html) => onChange("description", html)}
              placeholder="Décrivez l’événement, le lieu, les horaires, les informations pratiques..."
            />
          </AdminField>
        </div>

        <aside className="space-y-5">
          <div className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Aperçu vignette
              </p>
            </div>

            <div className="p-4">
              {previewUrl ? (
                <>
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="aspect-[16/10] w-full rounded-[1.25rem] object-cover"
                  />

                  <p className="mt-3 break-all text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {previewUrl}
                  </p>
                </>
              ) : (
                <div className="flex aspect-[16/10] flex-col items-center justify-center rounded-[1.25rem] bg-white text-center text-slate-400 dark:bg-slate-900 dark:text-slate-500">
                  <FiImage className="text-4xl" />

                  <p className="mt-3 text-sm font-bold">Aucune vignette</p>

                  <p className="mt-1 max-w-[220px] text-xs leading-5">
                    Ajoutez une image pour valoriser l’événement.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
              <FiCalendar />
              Récapitulatif
            </p>

            <div className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <p>
                <span className="font-bold text-slate-900 dark:text-white">
                  Début :
                </span>{" "}
                {startPreview}
              </p>

              {endPreview ? (
                <p className="mt-1">
                  <span className="font-bold text-slate-900 dark:text-white">
                    Fin :
                  </span>{" "}
                  {endPreview}
                </p>
              ) : null}

              {form.all_day ? (
                <p className="mt-2 rounded-xl bg-sky-50 px-3 py-2 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                  Événement sans horaire précis
                </p>
              ) : null}
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_active", !form.is_active)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_active
                    ? "Événement publié"
                    : "Événement en brouillon"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_active
                    ? "Il sera visible sur le site public."
                    : "Il restera masqué du public."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_active ? "bg-emerald-500" : "bg-amber-500"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_active ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-sky-100 bg-sky-50 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
            <p className="text-sm font-bold text-sky-900 dark:text-sky-200">
              Conseils pratiques
            </p>

            <ul className="mt-3 space-y-2">
              {EVENTS_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-sky-800 dark:text-sky-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}